import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { Btn } from "./kit";
import { Modal, Field, TextInput, TextArea } from "./Form";
import { CWeekPanel } from "./CWeekPanel";

const StandardItem = ({ item, i }) => (
  <li data-testid={`standard-item-${i}`} className="group relative rounded-2xl border border-[#1E293B] bg-[#0A0E1A] p-5 transition-colors hover:border-[#39FF14]/60">
    <span className="absolute right-4 top-4 font-mono-gd text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
    <h3 className="font-display text-2xl font-bold uppercase leading-none text-white">
      <span className="text-[#39FF14]">{item.name.charAt(0)}</span>{item.name.slice(1)}
    </h3>
    <p className="mt-3 text-sm leading-relaxed text-slate-300">{item.text}</p>
  </li>
);

const Editor = ({ open, onClose, doc, tid, level, onSaved }) => {
  const [f, setF] = useState(doc);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(doc); }, [open, doc]);
  const setItem = (i, k) => (e) => setF((x) => ({ ...x, items: x.items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)) }));
  const save = async (e) => {
    e.preventDefault();
    const items = f.items.map((it) => ({ name: it.name.trim(), text: it.text.trim() })).filter((it) => it.name && it.text);
    if (!f.title.trim() || !items.length) return toast.error("Add a title and at least one standard");
    setBusy(true);
    try {
      await web.put(`/teams/${tid}/standards/${level}`, { title: f.title.trim(), subtitle: f.subtitle?.trim() || null, items, closing: f.closing?.trim() || null });
      toast.success("Team standards saved");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (!f) return null;
  return (
    <Modal open={open} onClose={onClose} testid="standards-editor" title="Edit team standards" description="Everyone on this level sees this on their home page.">
      <form onSubmit={save} className="mt-2 space-y-4">
        <Field label="Title"><TextInput data-testid="standards-title-input" value={f.title} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} /></Field>
        <Field label="Tagline"><TextInput data-testid="standards-subtitle-input" value={f.subtitle ?? ""} onChange={(e) => setF((x) => ({ ...x, subtitle: e.target.value }))} /></Field>
        {f.items.map((it, i) => (
          <div key={i} className="space-y-2 rounded-xl border border-[#1E293B] p-3">
            <div className="flex gap-2">
              <TextInput data-testid={`standards-item-name-${i}`} value={it.name} onChange={setItem(i, "name")} placeholder="Name" />
              <button type="button" aria-label="Remove" data-testid={`standards-item-remove-${i}`} onClick={() => setF((x) => ({ ...x, items: x.items.filter((_, j) => j !== i) }))} className="rounded-full p-2 text-slate-400 hover:text-red-200"><Trash2 className="h-4 w-4" /></button>
            </div>
            <TextArea data-testid={`standards-item-text-${i}`} value={it.text} onChange={setItem(i, "text")} placeholder="What it means" />
          </div>
        ))}
        {f.items.length < 12 && <Btn type="button" variant="ghost" data-testid="standards-add-item" onClick={() => setF((x) => ({ ...x, items: [...x.items, { name: "", text: "" }] }))}><Plus className="h-4 w-4" />Add standard</Btn>}
        <Field label="Closing line"><TextInput data-testid="standards-closing-input" value={f.closing ?? ""} onChange={(e) => setF((x) => ({ ...x, closing: e.target.value }))} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="standards-save">{busy ? "Saving…" : "Save"}</Btn>
        </div>
      </form>
    </Modal>
  );
};

/** Team standards for a level (e.g. Coach EM's 7 C's on the JV dashboard). Renders nothing when none are set. */
export const TeamStandards = ({ tid, level, canEdit }) => {
  const s = useApi(tid && level && `/teams/${tid}/standards/${level}`, web);
  const [editing, setEditing] = useState(false);
  const doc = s.data && Array.isArray(s.data.items) ? s.data : null;
  if (!doc) return null;
  return (
    <section data-testid="team-standards" className="relative mb-8 overflow-hidden rounded-3xl border border-[#39FF14]/30 bg-[#0F172A] p-6 sm:p-8">
      <div aria-hidden className="pointer-events-none absolute -right-6 -top-10 select-none font-display text-[180px] font-black leading-none text-[#39FF14]/[0.06]">7C</div>
      <div className="relative flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">{level === "jv" ? "JV" : "Varsity"} standards</p>
          <h2 data-testid="team-standards-title" className="mt-2 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl">{doc.title}</h2>
          {doc.subtitle && <p data-testid="team-standards-subtitle" className="mt-3 text-base font-semibold text-slate-200 md:text-lg">{doc.subtitle}</p>}
        </div>
        {canEdit && <Btn variant="ghost" data-testid="team-standards-edit" onClick={() => setEditing(true)}><Pencil className="h-4 w-4" />Edit</Btn>}
      </div>
      <ol className="relative mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {doc.items.map((it, i) => <StandardItem key={i} item={it} i={i} />)}
        {doc.closing && (
          <li data-testid="team-standards-closing" className="flex items-center rounded-2xl bg-[#39FF14] p-5 sm:col-span-2 xl:col-span-1">
            <p className="font-display text-2xl font-black uppercase leading-tight text-[#0A0E1A]">{doc.closing}</p>
          </li>
        )}
      </ol>
      <CWeekPanel tid={tid} level={level} canEdit={canEdit} items={doc.items} closing={doc.closing} />
      {canEdit && <Editor open={editing} onClose={() => setEditing(false)} doc={doc} tid={tid} level={level} onSaved={s.reload} />}
    </section>
  );
};
